import React, {
  useEffect,
  useState
} from 'react';

import {
  Cloud,
  Folder,
  Globe2,
  Loader2,
  Save,
  X
} from 'lucide-react';

import type {
  ProjectProfile
} from '@kaizen/shared';


type EditableProject =
  Partial<
    Omit<
      ProjectProfile,
      'id' |
      'createdAt' |
      'updatedAt' |
      'userId'
    >
  >;


interface EditProjectModalProps {

  project:
    ProjectProfile;

  onClose:
    () => void;

  onSave:
    (
      updates:
        EditableProject
    ) => Promise<void>;
}


export const EditProjectModal:
React.FC<EditProjectModalProps> = ({
  project,
  onClose,
  onSave
}) => {

  const [
    isSaving,
    setIsSaving
  ] =
    useState(
      false
    );


  const [
    error,
    setError
  ] =
    useState<
      string |
      null
    >(
      null
    );


  const [
    name,
    setName
  ] =
    useState(
      project.name
    );


  const [
    description,
    setDescription
  ] =
    useState(
      project.description ||
      ''
    );


  const [
    localPath,
    setLocalPath
  ] =
    useState(
      project.localPath
    );


  const [
    buildCommand,
    setBuildCommand
  ] =
    useState(
      project.buildCommand
    );


  const [
    testCommand,
    setTestCommand
  ] =
    useState(
      project.testCommand ||
      ''
    );


  const [
    devCommand,
    setDevCommand
  ] =
    useState(
      project.devCommand ||
      ''
    );


  const [
    buildOutputDirectory,
    setBuildOutputDirectory
  ] =
    useState(
      project.buildOutputDirectory
    );


  const [
    cloudflareProjectName,
    setCloudflareProjectName
  ] =
    useState(
      project.cloudflareProjectName ||
      ''
    );


  const [
    productionUrl,
    setProductionUrl
  ] =
    useState(
      project.productionUrl ||
      (
        project.cloudflareProjectName
          ? `https://${project.cloudflareProjectName}.pages.dev`
          : ''
      )
    );


  const [
    previewUrl,
    setPreviewUrl
  ] =
    useState(
      project.previewUrl ||
      ''
    );


  const [
    productionEnabled,
    setProductionEnabled
  ] =
    useState(
      project.productionEnabled
    );


  const [
    previewEnabled,
    setPreviewEnabled
  ] =
    useState(
      project.previewEnabled
    );


  useEffect(
    () => {

      const listener =
        (
          event:
            KeyboardEvent
        ) => {

          if (
            event.key ===
              'Escape' &&
            !isSaving
          ) {

            onClose();
          }
        };


      window.addEventListener(
        'keydown',
        listener
      );


      return () => {

        window.removeEventListener(
          'keydown',
          listener
        );
      };
    },
    [
      isSaving,
      onClose
    ]
  );


  const save =
    async () => {

      if (
        !name.trim() ||
        !localPath.trim()
      ) {

        setError(
          'Project name and laptop folder are required.'
        );

        return;
      }


      setError(
        null
      );


      setIsSaving(
        true
      );


      try {

        await onSave(
          {
            name:
              name.trim(),

            description:
              description.trim() ||
              undefined,

            localPath:
              localPath.trim(),

            buildCommand:
              buildCommand.trim() ||
              'npm run build',

            testCommand:
              testCommand.trim() ||
              undefined,

            devCommand:
              devCommand.trim() ||
              undefined,

            buildOutputDirectory:
              buildOutputDirectory.trim() ||
              'dist',

            cloudflareProjectName:
              cloudflareProjectName.trim() ||
              undefined,

            productionUrl:
              productionUrl.trim() ||
              undefined,

            previewUrl:
              previewUrl.trim() ||
              undefined,

            productionEnabled,

            previewEnabled
          }
        );

      }
      catch (
        saveError
      ) {

        setError(
          saveError instanceof Error
            ? saveError.message
            : String(
                saveError
              )
        );


        setIsSaving(
          false
        );
      }
    };


  const inputClass =
    `
      w-full
      h-10
      rounded-lg
      border border-white/[0.08]
      bg-white/[0.035]
      px-3
      text-[11px]
      text-slate-200
      placeholder:text-slate-700
      outline-none
      transition-colors
      focus:border-indigo-400/35
      focus:bg-white/[0.05]
    `;


  const labelClass =
    `
      block
      mb-1.5
      text-[9px]
      font-medium
      uppercase
      tracking-[0.11em]
      text-slate-600
    `;


  return (

    <div
      className="
        fixed
        inset-0
        z-[90]
        bg-[#020817]/78
        backdrop-blur-md
        flex
        items-end
        sm:items-center
        justify-center
        sm:p-5
      "
    >

      <div
        className="
          w-full
          sm:max-w-2xl
          max-h-[92dvh]
          overflow-hidden
          rounded-t-[18px]
          sm:rounded-[18px]
          border
          border-white/10
          bg-slate-950/95
          shadow-[0_30px_100px_rgba(0,0,0,0.55)]
          flex
          flex-col
        "
      >

        <div
          className="
            h-14
            shrink-0
            px-4
            flex
            items-center
            justify-between
            border-b
            border-white/[0.07]
            bg-white/[0.025]
          "
        >

          <div>

            <div
              className="
                text-[12px]
                font-semibold
                text-slate-100
              "
            >
              Edit Project
            </div>

            <div
              className="
                mt-0.5
                text-[9px]
                text-slate-600
              "
            >
              Project registration and deployment metadata
            </div>

          </div>


          <button
            type="button"
            disabled={
              isSaving
            }
            onClick={
              onClose
            }
            className="
              w-9
              h-9
              rounded-lg
              border
              border-white/[0.07]
              bg-white/[0.025]
              text-slate-500
              hover:text-slate-200
              hover:bg-white/[0.05]
              flex
              items-center
              justify-center
              transition-colors
              disabled:opacity-40
            "
          >

            <X
              className="
                w-4
                h-4
              "
            />

          </button>

        </div>


        <div
          className="
            overflow-y-auto
            p-4
            sm:p-5
            space-y-5
          "
        >

          <section
            className="
              space-y-3
            "
          >

            <div
              className="
                text-[9px]
                uppercase
                tracking-[0.13em]
                font-semibold
                text-indigo-300
              "
            >
              Identity
            </div>


            <div>

              <label className={labelClass}>
                Project Name
              </label>

              <input
                value={
                  name
                }
                onChange={
                  event =>
                    setName(
                      event.target.value
                    )
                }
                className={inputClass}
              />

            </div>


            <div>

              <label className={labelClass}>
                Description
              </label>

              <textarea
                value={
                  description
                }
                onChange={
                  event =>
                    setDescription(
                      event.target.value
                    )
                }
                rows={3}
                className={`
                  ${inputClass}
                  h-auto
                  min-h-[76px]
                  py-2.5
                  resize-none
                `}
              />

            </div>

          </section>


          <section
            className="
              space-y-3
            "
          >

            <div
              className="
                flex
                items-center
                gap-1.5
                text-[9px]
                uppercase
                tracking-[0.13em]
                font-semibold
                text-indigo-300
              "
            >

              <Folder className="w-3 h-3" />

              Laptop

            </div>


            <div>

              <label className={labelClass}>
                Laptop Folder
              </label>

              <input
                value={
                  localPath
                }
                onChange={
                  event =>
                    setLocalPath(
                      event.target.value
                    )
                }
                className={`
                  ${inputClass}
                  font-mono
                `}
              />

            </div>


            <div
              className="
                grid
                grid-cols-1
                sm:grid-cols-2
                gap-3
              "
            >

              <div>

                <label className={labelClass}>
                  Build Command
                </label>

                <input
                  value={
                    buildCommand
                  }
                  onChange={
                    event =>
                      setBuildCommand(
                        event.target.value
                      )
                  }
                  className={`
                    ${inputClass}
                    font-mono
                  `}
                />

              </div>


              <div>

                <label className={labelClass}>
                  Build Output
                </label>

                <input
                  value={
                    buildOutputDirectory
                  }
                  onChange={
                    event =>
                      setBuildOutputDirectory(
                        event.target.value
                      )
                  }
                  className={`
                    ${inputClass}
                    font-mono
                  `}
                />

              </div>


              <div>

                <label className={labelClass}>
                  Test Command
                </label>

                <input
                  value={
                    testCommand
                  }
                  onChange={
                    event =>
                      setTestCommand(
                        event.target.value
                      )
                  }
                  className={`
                    ${inputClass}
                    font-mono
                  `}
                />

              </div>


              <div>

                <label className={labelClass}>
                  Dev Command
                </label>

                <input
                  value={
                    devCommand
                  }
                  onChange={
                    event =>
                      setDevCommand(
                        event.target.value
                      )
                  }
                  className={`
                    ${inputClass}
                    font-mono
                  `}
                />

              </div>

            </div>

          </section>


          <section
            className="
              space-y-3
            "
          >

            <div
              className="
                flex
                items-center
                gap-1.5
                text-[9px]
                uppercase
                tracking-[0.13em]
                font-semibold
                text-indigo-300
              "
            >

              <Cloud className="w-3 h-3" />

              Cloudflare Pages

            </div>


            <div>

              <label className={labelClass}>
                Cloudflare Project
              </label>

              <input
                value={
                  cloudflareProjectName
                }
                onChange={
                  event =>
                    setCloudflareProjectName(
                      event.target.value
                    )
                }
                className={`
                  ${inputClass}
                  font-mono
                `}
              />

            </div>


            <div>

              <label className={labelClass}>
                Production URL
              </label>

              <div
                className="
                  relative
                "
              >

                <Globe2
                  className="
                    absolute
                    left-3
                    top-1/2
                    -translate-y-1/2
                    w-3.5
                    h-3.5
                    text-slate-600
                  "
                />

                <input
                  value={
                    productionUrl
                  }
                  onChange={
                    event =>
                      setProductionUrl(
                        event.target.value
                      )
                  }
                  placeholder="https://project.pages.dev"
                  className={`
                    ${inputClass}
                    pl-9
                    font-mono
                  `}
                />

              </div>

            </div>


            <div>

              <label className={labelClass}>
                Preview URL
              </label>

              <input
                value={
                  previewUrl
                }
                onChange={
                  event =>
                    setPreviewUrl(
                      event.target.value
                    )
                }
                className={`
                  ${inputClass}
                  font-mono
                `}
              />

            </div>


            <div
              className="
                grid
                grid-cols-1
                sm:grid-cols-2
                gap-2
              "
            >

              <label
                className="
                  min-h-[48px]
                  px-3
                  rounded-lg
                  border
                  border-white/[0.07]
                  bg-white/[0.025]
                  flex
                  items-center
                  justify-between
                  gap-3
                  cursor-pointer
                "
              >

                <span
                  className="
                    text-[10px]
                    text-slate-400
                  "
                >
                  Production Enabled
                </span>

                <input
                  type="checkbox"
                  checked={
                    productionEnabled
                  }
                  onChange={
                    event =>
                      setProductionEnabled(
                        event.target.checked
                      )
                  }
                  className="
                    accent-indigo-500
                  "
                />

              </label>


              <label
                className="
                  min-h-[48px]
                  px-3
                  rounded-lg
                  border
                  border-white/[0.07]
                  bg-white/[0.025]
                  flex
                  items-center
                  justify-between
                  gap-3
                  cursor-pointer
                "
              >

                <span
                  className="
                    text-[10px]
                    text-slate-400
                  "
                >
                  Preview Enabled
                </span>

                <input
                  type="checkbox"
                  checked={
                    previewEnabled
                  }
                  onChange={
                    event =>
                      setPreviewEnabled(
                        event.target.checked
                      )
                  }
                  className="
                    accent-indigo-500
                  "
                />

              </label>

            </div>

          </section>


          {error && (

            <div
              className="
                rounded-lg
                border
                border-rose-400/15
                bg-rose-400/[0.07]
                px-3
                py-2.5
                text-[10px]
                text-rose-300
              "
            >
              {error}
            </div>

          )}

        </div>


        <div
          className="
            shrink-0
            p-3
            sm:p-4
            border-t
            border-white/[0.07]
            bg-white/[0.025]
            flex
            justify-end
            gap-2
          "
        >

          <button
            type="button"
            disabled={
              isSaving
            }
            onClick={
              onClose
            }
            className="
              min-h-[40px]
              px-4
              rounded-lg
              border
              border-white/[0.07]
              bg-white/[0.025]
              text-[10px]
              font-medium
              text-slate-400
              disabled:opacity-40
            "
          >
            Cancel
          </button>


          <button
            type="button"
            disabled={
              isSaving
            }
            onClick={
              () =>
                void save()
            }
            className="
              min-h-[40px]
              px-4
              rounded-lg
              border
              border-indigo-400/20
              bg-indigo-600
              text-[10px]
              font-semibold
              text-white
              flex
              items-center
              gap-2
              disabled:opacity-40
              active:scale-[0.98]
            "
          >

            {isSaving
              ? (
                <Loader2
                  className="
                    w-3.5
                    h-3.5
                    animate-spin
                  "
                />
              )
              : (
                <Save
                  className="
                    w-3.5
                    h-3.5
                  "
                />
              )}


            {isSaving
              ? 'Saving…'
              : 'Save Changes'}

          </button>

        </div>

      </div>

    </div>
  );
};